import { makeStyles } from "@material-ui/core/styles";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Switch, TextField, Typography, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useSelector } from 'react-redux';
import { filternewsService } from '../services';

// "Console": the ADMIN/MODERATOR-only publisher filter. Adds a spamming
// publisher's name to the backend FilterNews list, and lists the existing
// rows with an active toggle and a delete.
//
// Built on the same dialog idiom as CardConfigPopup and the ~10
// Draggable*Popup components in SpaceItem.js - MUI Dialog plus a local
// PaperComponent wrapping react-draggable. Only the dialog SHAPE is shared
// with CardConfigPopup; nothing sortable, no @dnd-kit, no baseline/dirty
// machinery and no loading popup, because every call here is a plain
// database read or write.
//
// State is entirely component-local: no Redux slice, no store shape. The
// component is mounted on open by Menu.js, so each open re-reads the list.

// Mirrors the backend's FilterNews.NAME_MAX_LENGTH, re-validated server-side
// as empty / >120 / control characters -> 400 filternews.name.invalid.
const MAX_NAME = 120;

// createdby and createdat are null on every row that predates this feature.
// A muted em dash, so the gap is visibly "not recorded" rather than blank.
const NOT_RECORDED = '—';

// Written as a charCode scan rather than a regex on purpose: a character
// class containing control characters trips eslint's no-control-regex (a
// warning in eslint-config-react-app). Same form as CardConfigPopup.
function hasControlCharacter(value) {
    for (let i = 0; i < value.length; i++) {
        const code = value.charCodeAt(i);
        if (code < 32 || code === 127) {
            return true;
        }
    }
    return false;
}

// -> a user-facing message, or null when the name is acceptable. Saves a
// round trip on a predictably invalid name; the server re-checks all three.
//
// Note what is deliberately NOT checked here: whether the name already
// exists. The backend match is case-SENSITIVE while the database collation
// on filter_news.name is case-insensitive, so two rows can legitimately
// differ only by case and a client-side duplicate check would have to pick
// one of the two comparisons and be wrong about the other. The add is
// idempotent server-side anyway.
function nameProblem(trimmed) {
    if (trimmed.length === 0) {
        return 'Please type a publisher name first.';
    }
    if (trimmed.length > MAX_NAME) {
        return 'Please use ' + MAX_NAME + ' characters or fewer.';
    }
    if (hasControlCharacter(trimmed)) {
        return 'Please remove the special characters from the name.';
    }
    return null;
}

// Only ever the machine-readable error key. NOT body.message (prefixed at
// runtime with a random "[Error number: ffnpgtd]") and NOT body.status (the
// enum NAME string, e.g. "UNAUTHORIZED", not a number). Array-guarded
// because a request rejected by Spring before the controller runs answers
// with Boot's default error body, which has no `errors` key at all, and
// because errors[0] itself can be null on a 500.
function firstError(body) {
    if (body && Array.isArray(body.errors) && body.errors.length > 0) {
        return body.errors[0];
    }
    return undefined;
}

const useConsoleStyles = makeStyles(theme => ({
    paper: {
        overflowY: 'hidden',
        overflowX: 'hidden',
        width: 320,
        '@media (min-width: 600px) and (max-width: 800px)': {
            width: 500
        },

        '@media (min-width: 800px)': {
            width: 600
        }
    }
}));

// The `cancel` selector keeps the dialog's own dragger from claiming
// pointer/touch events that start inside DialogContent, where the input and
// the rows live. All dialog draggers in this repo carry it.
function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

// One filter row: the publisher name, who added it and when, an active
// toggle, and a delete with an inline two-step confirm.
const FilterRow = ({ filter, narrow, dark, mutedColor, textColor, busy, confirming, onToggle, onAskDelete, onCancelDelete, onConfirmDelete }) => {
    const buttonSize = narrow ? 'small' : 'medium';
    const iconSize = narrow ? 'small' : 'medium';
    const buttonStyle = narrow ? { padding: 2, color: dark ? '#fff' : '#000' } : { color: dark ? '#fff' : '#000' };

    return (
        <Stack direction="row" alignItems="center" spacing={0} style={{ paddingTop: 2, paddingBottom: 2 }}>
            <Box style={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                    component="div"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: textColor, fontSize: narrow ? '0.9rem' : '1rem' }}
                    // The full value, because the exact string is what makes
                    // the filter work and an ellipsis would hide a typo.
                    title={filter.name}
                >
                    {filter.name}
                </Typography>
                <Typography
                    component="div"
                    variant="caption"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: mutedColor }}
                >
                    {/* createdat is a Java Date.toString() string
                        ("Sat Oct 11 17:06:51 CEST 2026"), not ISO-8601 - it
                        is rendered exactly as received and never parsed. */}
                    {filter.createdby ? filter.createdby : NOT_RECORDED}
                    {' · '}
                    {filter.createdat ? filter.createdat : NOT_RECORDED}
                </Typography>
            </Box>

            {confirming ?
                // Two-step delete, inline in this same popup - no second
                // dialog file for one confirmation. The Remove/Keep pair
                // replaces the row's own controls while it is standing.
                <>
                    <Button size="small" variant="contained" color="error" disabled={busy} onClick={() => onConfirmDelete(filter)} style={{ textTransform: 'none', minWidth: 0 }}>
                        Remove
                    </Button>
                    <Button size="small" disabled={busy} onClick={onCancelDelete} style={{ textTransform: 'none', minWidth: 0, color: dark ? '#fff' : '#000' }}>
                        Keep
                    </Button>
                </>
                :
                <>
                    <Switch
                        size={narrow ? 'small' : 'medium'}
                        checked={!!filter.active}
                        disabled={busy}
                        onChange={() => onToggle(filter)}
                        inputProps={{ 'aria-label': (filter.active ? 'Switch off the filter for ' : 'Switch on the filter for ') + filter.name }}
                        title={filter.active ? 'Applied - switch off to keep it without filtering' : 'Not applied - switch on to filter again'}
                    />
                    <IconButton size={buttonSize} style={buttonStyle} disabled={busy} onClick={() => onAskDelete(filter)} aria-label={"Remove " + filter.name} title="Remove this filter for good">
                        <DeleteOutlineIcon fontSize={iconSize} />
                    </IconButton>
                </>
            }
        </Stack>
    );
}

export const ConsolePopup = ({ open, onClose, onSessionProblem }) => {
    const classes = useConsoleStyles();

    const t = useSelector(state => state.themeswitch);
    const [theme, setTheme] = useState(t);

    useEffect(() => {
        setTheme(t);
    }, [t])

    const dark = theme === 'dark';

    const narrow = useMediaQuery('(max-width:420px)');

    const [filters, setFilters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [failure, setFailure] = useState(null);
    const [adding, setAdding] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    // handleFailure re-fetches the list on several branches, and the fetch
    // reports its own failures back through handleFailure - mutually
    // recursive by design. The ref breaks the dependency cycle without an
    // exhaustive-deps suppression.
    const loadRef = useRef(null);
    const reload = useCallback(() => {
        if (loadRef.current) {
            loadRef.current();
        }
    }, []);

    // Branches ONLY on the HTTP status code and firstError(body).
    const handleFailure = useCallback((op, result) => {
        const status = result ? result.status : 0;
        const code = firstError(result && result.body);

        if (status === 401 || status === 403) {
            // The role claim is baked into a token that lives 365 days,
            // while the menu gate reads user.role from the login response -
            // so a user granted ADMIN after their token was issued renders
            // the entry and 403s on every call. There is no refresh-token
            // flow in this codebase, so the only honest remedy is a fresh
            // sign-in.
            //
            // Both statuses are keyed on the status ALONE, deliberately
            // unlike the 404 and the three 400s below: a 401 or 403 that
            // carries some other code (or none at all, from something in
            // front of the backend) still means the session cannot do this,
            // so it must lock out rather than fall through to a generic
            // "please try again" that never will.
            //
            // No retry here, and nothing swallowed: onSessionProblem tells
            // Menu.js to stop rendering the entry for the rest of the
            // session and to name the remedy.
            setConfirmDeleteId(null);
            onClose();
            if (onSessionProblem) {
                onSessionProblem();
            }
            return;
        }
        if (status === 400 && code === 'filternews.name.invalid') {
            // The popup stays open so the name can be corrected.
            setNameError('That publisher name cannot be used. Please use 1 to ' + MAX_NAME + ' ordinary characters.');
            return;
        }
        if (status === 400 && (code === 'filternews.id.missing' || code === 'filternews.active.missing')) {
            // Nothing the user did: the request left here incomplete.
            console.error('ConsolePopup: incomplete ' + op + ' request rejected by the server (' + code + ')');
            setFailure('Something went wrong here. Please close this window and open it again.');
            return;
        }
        if (status === 0) {
            // The service's network/DNS/CORS marker, not an HTTP status.
            if (op === 'list') {
                setFailure("Can't reach OneLike. Please check your connection and try again.");
                return;
            }
            // A write whose outcome is genuinely unknown: it may well have
            // been committed server-side, so this is explicitly NOT reported
            // as a failure. Re-read the list and let it answer.
            setNotice('The connection dropped, so this may or may not have been saved. The list below has been re-read.');
            reload();
            return;
        }
        // 500 (where errors[0] varies and may be null), and anything
        // unmapped - including a 404 that is NOT filternews.notfound, such as
        // a gateway's own 404 page, which must never be swallowed as
        // "already deleted".
        setFailure('Something went wrong on our side. Please try again.');
        if (op !== 'list') {
            reload();
        }
    }, [onClose, onSessionProblem, reload]);

    const load = useCallback(() => {
        setLoading(true);
        return filternewsService.getFilters().then(result => {
            setLoading(false);
            if (result.ok) {
                // Taken in the order received: the server sorts id
                // descending. Never re-sorted, and never deduped - the
                // database collation is case-insensitive while the Java
                // match is case-sensitive, so two rows differing only by
                // case are two legitimately different filters and both must
                // show. The backend id is the React key.
                setFilters((result.body && Array.isArray(result.body.filters)) ? result.body.filters : []);
                return;
            }
            handleFailure('list', result);
        });
    }, [handleFailure]);

    useEffect(() => {
        loadRef.current = load;
    }, [load]);

    // Mounted on open by Menu.js, so this runs once per open - which is
    // exactly the intended re-read, with no reseed guard to get wrong.
    useEffect(() => {
        if (open) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const busy = adding || loading || busyId !== null;

    const handleAdd = () => {
        // The only double-submit guard, per spec: the button and the Enter
        // key are both blocked while the add is in flight. The add is
        // idempotent server-side, so a second one would be a success too.
        if (adding) {
            return;
        }
        const trimmed = name.trim();
        const problem = nameProblem(trimmed);
        if (problem) {
            setNameError(problem);
            return;
        }
        setNameError(null);
        setNotice(null);
        setFailure(null);
        setAdding(true);

        filternewsService.addFilter(trimmed).then(result => {
            setAdding(false);
            if (result.ok) {
                setName('');
                // Three success shapes, none of them an error:
                //   added true                    -> a new row
                //   reactivated true              -> existed inactive, now on
                //   both false                    -> already on and active
                const added = !!(result.body && result.body.added);
                const reactivated = !!(result.body && result.body.reactivated);
                if (added) {
                    setNotice('"' + trimmed + '" is now filtered.');
                } else if (reactivated) {
                    setNotice('"' + trimmed + '" was already on the list but switched off. It is on again now.');
                } else {
                    setNotice('"' + trimmed + '" is already on the list and already active. Nothing changed.');
                }
                reload();
                return;
            }
            handleFailure('add', result);
        });
    };

    const handleToggle = (filter) => {
        if (busy) {
            return;
        }
        const next = !filter.active;
        setNameError(null);
        setNotice(null);
        setFailure(null);
        setBusyId(filter.id);

        filternewsService.setActive(filter.id, next).then(result => {
            setBusyId(null);
            if (result.ok) {
                // Replaced in place, so the server's id-descending order is
                // preserved. The echoed row is authoritative where present.
                const updated = result.body && result.body.filter;
                setFilters(current => current.map(f => (f.id === filter.id ? (updated ? updated : { ...f, active: next }) : f)));
                setNotice(next
                    ? '"' + filter.name + '" is filtered again.'
                    : '"' + filter.name + '" is kept on the list but no longer filtered.');
                return;
            }
            if (result.status === 404 && firstError(result.body) === 'filternews.notfound') {
                // Already deleted elsewhere: drop it locally, show no error,
                // then re-read. Status AND code, so any other 404 falls
                // through to the generic branch instead.
                setFilters(current => current.filter(f => f.id !== filter.id));
                setConfirmDeleteId(null);
                reload();
                return;
            }
            handleFailure('active', result);
        });
    };

    const handleConfirmDelete = (filter) => {
        if (busy) {
            return;
        }
        setNameError(null);
        setNotice(null);
        setFailure(null);
        setBusyId(filter.id);

        filternewsService.deleteFilter(filter.id).then(result => {
            setBusyId(null);
            if (result.ok) {
                setFilters(current => current.filter(f => f.id !== filter.id));
                setConfirmDeleteId(null);
                setNotice('"' + filter.name + '" has been removed from the list.');
                return;
            }
            if (result.status === 404 && firstError(result.body) === 'filternews.notfound') {
                setFilters(current => current.filter(f => f.id !== filter.id));
                setConfirmDeleteId(null);
                reload();
                return;
            }
            handleFailure('delete', result);
        });
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
        setNameError(null);
    };

    const handleNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    const textColor = dark ? '#fff' : '#000';
    const mutedColor = dark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)';
    const surface = dark ? '#0D1F2D' : '#fff';

    return (
        <div>
            <Dialog
                open={open}
                onClose={busy ? undefined : onClose}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
                classes={{ paper: classes.paper }}
            >
                <DialogTitle style={{ cursor: 'move', color: textColor, background: surface }} id="draggable-dialog-title">
                    <span>Filter a publisher</span>
                </DialogTitle>
                {/* Everything interactive lives in DialogContent, which the
                    PaperComponent's `cancel` selector excludes from the
                    dialog's own dragger. */}
                <DialogContent style={{ backgroundColor: surface, color: textColor, overflowY: 'auto', paddingLeft: narrow ? 8 : 16, paddingRight: narrow ? 8 : 16 }}>
                    {/* The three paragraphs, the helper text, the placeholder
                        and the button label below are PINNED copy. There is
                        no feedback mechanism anywhere in this feature - a
                        mistyped name silently filters nothing - so these
                        strings are the only thing between an admin and a
                        useless filter. Do not paraphrase or shorten them. */}
                    <Box style={{ paddingTop: 8, paddingBottom: 8 }}>
                        <Typography variant="body2" style={{ color: mutedColor, marginBottom: 6 }}>
                            Enter the publisher's name <strong>exactly as it appears after the dash at the end of a Google News headline</strong>.
                        </Typography>
                        <Typography variant="body2" style={{ color: mutedColor, marginBottom: 6 }}>
                            For a headline ending <code>… - The Hacker News</code>, type <code>The Hacker News</code> — not <code>thehackernews.com</code>.
                        </Typography>
                        <Typography variant="body2" style={{ color: mutedColor, marginBottom: 6 }}>
                            Matching is case-sensitive and must match exactly. <code>the hacker news</code> will not filter anything.
                        </Typography>

                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <TextField
                                id="console-filter-name"
                                label="Publisher name"
                                placeholder="The Hacker News"
                                value={name}
                                onChange={handleNameChange}
                                onKeyDown={handleNameKeyDown}
                                error={!!nameError}
                                helperText="The filter applies as each news card refreshes — typically within the hour, not immediately."
                                // Kept muted even while the field is in its
                                // error state: the helper text is pinned copy
                                // and must stay readable as guidance, so the
                                // inline error gets its own line below.
                                FormHelperTextProps={{ style: { color: mutedColor } }}
                                size="small"
                                fullWidth
                                disabled={adding}
                                inputProps={{ maxLength: MAX_NAME, autoComplete: 'off', spellCheck: false }}
                                sx={{
                                    '& .MuiInputBase-input': { color: textColor },
                                    '& .MuiInputLabel-root': { color: mutedColor },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAdd}
                                disabled={adding}
                                startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                style={{ marginTop: 2, textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                Add filter
                            </Button>
                        </Stack>
                        {nameError ?
                            <Typography variant="body2" style={{ color: '#d32f2f' }}>
                                {nameError}
                            </Typography>
                            : null}
                    </Box>

                    {notice ?
                        <Typography variant="body2" style={{ color: textColor, background: dark ? 'rgba(25,118,210,0.22)' : 'rgba(25,118,210,0.10)', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                            {notice}
                        </Typography>
                        : null}
                    {failure ?
                        <Typography variant="body2" style={{ color: '#fff', background: '#d32f2f', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                            {failure}
                        </Typography>
                        : null}

                    <Typography variant="body2" style={{ color: mutedColor, marginBottom: 4 }}>
                        A filter that is switched off stays on the list but is not applied. An older filter can show as off even though nobody switched it off - switching it on repairs it for good.
                    </Typography>

                    {filters.map(filter => (
                        <FilterRow
                            key={filter.id}
                            filter={filter}
                            narrow={narrow}
                            dark={dark}
                            mutedColor={mutedColor}
                            textColor={textColor}
                            busy={busy}
                            confirming={confirmDeleteId === filter.id}
                            onToggle={handleToggle}
                            onAskDelete={(f) => { setConfirmDeleteId(f.id); setNotice(null); setFailure(null); }}
                            onCancelDelete={() => setConfirmDeleteId(null)}
                            onConfirmDelete={handleConfirmDelete}
                        />
                    ))}

                    {filters.length === 0 ?
                        <Typography variant="body2" style={{ color: mutedColor }}>
                            {loading ? 'Reading the filter list...' : 'No publishers are filtered yet.'}
                        </Typography>
                        : null}
                </DialogContent>
                <DialogActions style={{ color: '#fff', background: surface }} > {/* light */}
                    <Button onClick={onClose} variant="contained" disabled={busy}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
